import { LimitConfig } from './types';

/**
 * Minimal shape for the LAMFDetails `offers[]` entries. We rely on
 * `creditLimit` (slider bounds) and `interestRate` (rate-zone labels).
 */
export type LimitOffer = {
  offerCode?: string;
  creditLimit: number;
  interestRate?: number;
  [key: string]: any;
};

/**
 * Build slider config from the LAMFDetails payload. The `offers[]` array is
 * the single source of truth for the progress bar:
 *
 *   Two offers  ─ Higher-LTV / multi-tier flow
 *     • `maxAmount`     ← upper offer's `creditLimit`
 *     • `rateThreshold` ← lower offer's `creditLimit` (visible breakpoint)
 *     • `lowRate`       ← lower offer's `interestRate`
 *     • `highRate`      ← upper offer's `interestRate`
 *     • `hasRateThreshold` = true
 *
 *   One offer   ─ Single-tier flow (no breakpoint)
 *     • `maxAmount`     ← offer's `creditLimit`
 *     • `rateThreshold` ← `maxAmount` (so `getRate` always returns `lowRate`)
 *     • `lowRate` = `highRate` ← offer's `interestRate`
 *     • `hasRateThreshold` = false
 */
export const getLimitConfig = (
  minAvailableLimit?: number,
  availableCreditAmount?: number,
  offers?: LimitOffer[],
): LimitConfig => {
  const sortedOffers = offers?.length
    ? [...offers].sort((a, b) => a.creditLimit - b.creditLimit)
    : undefined;
  const lowerOffer = sortedOffers?.[0];
  const upperOffer = sortedOffers?.[sortedOffers.length - 1];
  const hasTwoOffers = !!sortedOffers && sortedOffers.length >= 2;

  const maxAmount = availableCreditAmount ?? upperOffer?.creditLimit ?? 0;
  const minAmount = minAvailableLimit ?? 10000;
  const isCompact = maxAmount <= 20000;
  const step = isCompact ? 500 : 1000;

  // Threshold visible only when a distinct lower offer sits strictly inside
  // the slider range. Otherwise we collapse to a single-tier gradient.
  const hasRateThreshold =
    hasTwoOffers &&
    !!lowerOffer &&
    lowerOffer.creditLimit > minAmount &&
    lowerOffer.creditLimit < maxAmount;

  const rateThreshold = hasRateThreshold
    ? (lowerOffer as LimitOffer).creditLimit
    : maxAmount;

  const lowRate = hasRateThreshold
    ? (lowerOffer as LimitOffer).interestRate ?? 0
    : upperOffer?.interestRate ?? 0;
  const highRate = hasRateThreshold
    ? upperOffer?.interestRate ?? 0
    : lowRate;

  return {
    minAmount,
    maxAmount,
    rateThreshold,
    step,
    scale: isCompact ? 'compact' : 'standard',
    lowRate,
    highRate,
    hasRateThreshold,
  };
};

export const getRate = (amount: number, config: LimitConfig) =>
  amount > config.rateThreshold ? config.highRate : config.lowRate;

export const getMonthlyEmi = (amount: number, config: LimitConfig) =>
  Math.round((amount * getRate(amount, config)) / 100 / 12);

/** Clamp to min/max only — preserves exact values (no step rounding). */
export const clampAmount = (value: number, config: LimitConfig) =>
  Math.min(Math.max(value, config.minAmount), config.maxAmount);

export const getAmountRangeValidationMessage = (
  amountText: string,
  config: LimitConfig,
  formatLabel: (amount: number) => string,
): string | null => {
  const rangeLabel = `Please enter a value between ${formatLabel(config.minAmount)} & ${formatLabel(config.maxAmount)}`;

  if (!amountText) {
    return rangeLabel;
  }

  const digitsOnly = amountText.replace(/\D/g, '');
  if (!digitsOnly) {
    return rangeLabel;
  }

  const value = parseInt(digitsOnly, 10);
  if (
    Number.isNaN(value) ||
    value < config.minAmount ||
    value > config.maxAmount
  ) {
    return rangeLabel;
  }

  return null;
};
